import React from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { compose } from "react-apollo";
import { Table, Header, Label } from "semantic-ui-react";
import { Trans } from "react-i18next";
import { format } from "date-fns";
import classNames from "classnames";

import { FORMAT_TIME } from "../../common/dateFormats";
import { ROLE_MEMBER, ROLE_SYSTEM_ADMINISTRATOR } from "../../security/role";
import RegisterContainer from "./register/RegisterContainer";
import CancelContainer from "./cancel/CancelContainer";

function ByDay({
  date,
  onSelectTrainingUnit,
  trainingUnits,
  onRefreshData,
  myRole
}) {
  const locationWithOfftime = {};

  for (let trainingUnit of trainingUnits) {
    const locationId = trainingUnit.courseDate.location.id;
    if (
      trainingUnit.offTimeReason !== null &&
      !locationWithOfftime[locationId]
    ) {
      locationWithOfftime[locationId] = {
        location: trainingUnit.courseDate.location,
        offTimeReason: trainingUnit.offTimeReason
      };
    }
  }

  // Administrator should see all training units, members only see training units without off time
  const filteredTrainingUnits =
    myRole === ROLE_SYSTEM_ADMINISTRATOR
      ? trainingUnits
      : trainingUnits.filter(tu => tu.offTimeReason === null);

  return (
    <>
      <Header as="h3" className="fitKiel-courses-coursePlan-list__header">
        <Trans i18nKey={`date.weekdays.${format(date, "i")}`} />
        <span className="fitKiel-courses-coursePlan-list__header-date">
          {format(date, "dd.")}{" "}
          <Trans i18nKey={`date.months.${format(date, "M")}`} />{" "}
          {format(date, "yyyy")}
        </span>
      </Header>
      <div className="scrolling contentNoMargin">
        <Table className="fitKiel-courses-coursePlan-list__table" unstackable>
          <Table.Body>
            {myRole === ROLE_MEMBER &&
              Object.keys(locationWithOfftime).map(locationId => {
                const { location, offTimeReason } = locationWithOfftime[
                  locationId
                ];
                return (
                  <Table.Row
                    key={locationId}
                    verticalAlign="middle"
                    className="fitKiel-courses-coursePlan-list__row"
                    style={{ pointerEvents: "none" }}
                  >
                    <Table.Cell
                      width={2}
                      className="fitKiel-courses-coursePlan-list__course-time"
                    ></Table.Cell>
                    <Table.Cell>
                      <div className="fitKiel-courses-coursePlan-list__course-title">
                        <span>
                          {offTimeReason ? (
                            <Trans
                              i18nKey="coursePlan.index.locationOffTimeWithReason"
                              values={{
                                location: location.name,
                                reason: offTimeReason
                              }}
                            />
                          ) : (
                            <Trans
                              i18nKey="coursePlan.index.locationOffTime"
                              values={{
                                location: location.name
                              }}
                            />
                          )}
                        </span>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })}

            {filteredTrainingUnits.map(trainingUnit => {
              let nonActive = false;

              if (!trainingUnit.active) {
                nonActive = true;
              } else if (myRole !== ROLE_SYSTEM_ADMINISTRATOR) {
                if (
                  trainingUnit.canNotRegisterCause !== "alreadyRegistered" &&
                  !trainingUnit.canRegister
                ) {
                  nonActive = true;
                }
              }

              const rowClasses = classNames({
                "fitKiel-courses-coursePlan-list__row": true,
                "fitKiel-courses-coursePlan-list__row--nonActive": nonActive,
                "fitKiel-courses-coursePlan-list__row--isRegistered":
                  trainingUnit.isRegistered,
                "fitKiel-courses-coursePlan-list__row--isOnWaitingList":
                  trainingUnit.waitingListPosition > 0
              });

              const maximumParticipants =
                trainingUnit.courseDate.maximumParticipants;

              const displayReservedSeats = Math.min(
                trainingUnit.reservedSeats,
                maximumParticipants
              );

              const waitingListSeats = Math.max(
                0,
                trainingUnit.reservedSeats - maximumParticipants
              );

              return (
                <Table.Row
                  key={trainingUnit.id}
                  verticalAlign="middle"
                  className={rowClasses}
                >
                  <Table.Cell
                    width={2}
                    onClick={() => onSelectTrainingUnit(trainingUnit.id)}
                    className="fitKiel-courses-coursePlan-list__course-time"
                  >
                    {format(new Date(trainingUnit.date), FORMAT_TIME)}
                  </Table.Cell>
                  <Table.Cell
                    onClick={() => onSelectTrainingUnit(trainingUnit.id)}
                  >
                    <div className="fitKiel-courses-coursePlan-list__course-title">
                      {trainingUnit.courseDate.course.title}{" "}
                      {trainingUnit.internalCancelReason && (
                        <span>
                          |
                          <em>
                            {" "}
                            <Trans
                              i18nKey={`coursePlan.index.internalCancelReasons.${
                                trainingUnit.internalCancelReason
                              }${
                                (trainingUnit.offTimeReason || trainingUnit.cancelCause) ? "WithReason" : ""
                              }`}
                              values={{
                                location: trainingUnit.courseDate.location.name,
                                reason: trainingUnit.offTimeReason || trainingUnit.cancelCause
                              }}
                            />
                          </em>
                        </span>
                      )}
                    </div>
                    <div>
                      <em>
                        {trainingUnit.courseDate.location.name} /{" "}
                        <Trans
                          i18nKey="coursePlan.labels.duration"
                          values={{
                            duration: trainingUnit.courseDate.course.duration
                          }}
                        />
                      </em>
                    </div>
                  </Table.Cell>
                  <Table.Cell width={1} collapsing>
                    {trainingUnit.hasTrialRegistration ? (
                      <Label basic color="blue">
                        <Trans
                          i18nKey="coursePlan.labels.hasTrialRegistration"
                          values={{
                            duration: trainingUnit.courseDate.course.duration
                          }}
                        />
                      </Label>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell
                    width={2}
                    collapsing
                    textAlign="left"
                    onClick={() => onSelectTrainingUnit(trainingUnit.id)}
                  >
                    <Label size="large" basic>
                      {displayReservedSeats} / {maximumParticipants}
                    </Label>
                    {waitingListSeats > 0 && (
                      <Label size="large" circular color="blue">
                        +{waitingListSeats}
                      </Label>
                    )}
                  </Table.Cell>
                  {myRole === ROLE_MEMBER && (
                    <Table.Cell width={3} collapsing textAlign="right">
                      {trainingUnit.isRegistered ? (
                        <CancelContainer
                          trainingUnitId={trainingUnit.id}
                          canCancel={trainingUnit.canCancel}
                          courseTitle={trainingUnit.courseDate.course.title}
                          trainingUnitDate={trainingUnit.date}
                          isOnWaitingList={trainingUnit.waitingListPosition > 0}
                          onFinished={onRefreshData}
                        />
                      ) : (
                        <RegisterContainer
                          trainingUnit={trainingUnit}
                          onFinished={onRefreshData}
                        />
                      )}
                    </Table.Cell>
                  )}
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </div>
    </>
  );
}

ByDay.displayName = "coursePlan/ByDay";

const mapStateToProps = ({ auth: { role } }) => ({
  myRole: role
});

export default compose(connect(mapStateToProps), withRouter)(React.memo(ByDay));
